import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import Confetti from 'react-confetti';
import { useNavigate, useParams } from 'react-router-dom';
import { Pie, PieChart, Tooltip as Tol } from 'recharts';
import { TimeProgressBar } from '../../vendor/TimeProgressBar/TimeProgressBar';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ReorderIcon from '@mui/icons-material/Reorder';
import SaveIcon from '@mui/icons-material/Save';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Box,
  Button,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

// Redux wrappers
import AddScopeDialog from '../../redux/containers/project/AddScopeDialog';
import EditMembersDialog from '../../redux/containers/project/EditMembersDialog';
import EditProjectDialog from '../../redux/containers/project/EditProjectDialog';
import EditTagsDialog from '../../redux/containers/project/EditTagsDialog';
//My components
import Scope from '../../redux/containers/project/Scope';
import { IconGradientDefinition, getIAStyles } from '../../utils/iaDesign'
import { sortTasks } from '../../utils/methods';
import DragabbleCard from './DragabbleCard';
import Chart from './hillChart/Chart';
import DeleteDialog from '../DeleteDialog';
import ScrollToTop from '../home/ScrollToTop';

export default function Project(props) {
  const {
    hasScopes,
    isLoadingScopes,
    scopes,
    hasUsers,
    projects,
    project,
    setProject,
    loadingUpdate,
    getProjects,
    postSnapshots,
    getScopes,
    deleteProject,
    updateScopes,
    getRole,
    userInfo,
    updateTask,
    isScopesSuggestionsLoading,
    getScopesSuggestions,
    scopesSuggestions,
    saveSuggestedScopes,
    isLoadingTaskSuggestions,
  } = props;

  const { id } = useParams();
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState();
  const [projectExists, setProjectExists] = useState(false);

  const admin = getRole.admin;
  let users = props.users;
  const username = userInfo.name;
  const navigate = useNavigate();
  const [width, setWidth] = useState(window.innerWidth / 1.3);
  const [height, setHeight] = useState(window.innerHeight / 3.2);
  const [preview, setPreview] = useState(true);
  const [openScopeDialog, setOpenScopeDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openMemberDialog, setOpenMemberDialog] = useState(false);
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [snapshotDict, setSnapshotDict] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [move, setMove] = useState(false);
  const [position, setPosition] = useState(-1);
  const [showHistory, setShowHistory] = useState(false);
  const openMenu = Boolean(anchorEl);
  window.onresize = resize;
  const confettiWidth = window.innerWidth;
  const confettiHeight = window.innerHeight;
  const [runConfetti, setRunConfetti] = useState(false);
  const [localScopes, setLocalScopes] = useState(null);
  const [suggestingScopeId, setSuggestingScopeId] = useState(null);
  const [isSavingSuggestions, setIsSavingSuggestions] = useState(false);
  const hasSuggestions = localScopes?.scopes?.some(s => s.is_suggestion);
  useEffect(() => {
    setLocalScopes((prevLocalScopes) => {
      // If there is no new data from the props, return the current state
      if (!scopes || !scopes.scopes) return scopes;
      // Retain any AI suggestions that are currently present in the local state
      const currentSuggestions = prevLocalScopes?.scopes?.filter(s => s.is_suggestion) || [];
      // Merge the fresh database scopes with the retained local AI suggestions
      return {
        ...scopes,
        scopes: [...(scopes.scopes || []), ...currentSuggestions]
      };
    });
  }, [scopes]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openScope = () => {
    setOpenScopeDialog(true);
  };

  const closeScope = () => {
    setOpenScopeDialog(false);
  };

  const handleHistory = () => {
    setPosition(-1);
    setShowHistory(!showHistory);
  };

  const handleLeft = history_length => {
    if (position - 1 >= -history_length) {
      setPosition(position - 1);
    }
  };

  const handleRight = () => {
    if (position + 1 < 0) {
      setPosition(position + 1);
    }
  };

  const handleForwardLeft = history_length => {
    setPosition(-history_length);
  };

  const handleForwardRight = () => {
    setPosition(-1);
  };

  const handleEdit = () => {
    handleClose();
    setOpenEditDialog(!openEditDialog);
  };

  const handleMembers = () => {
    handleClose();
    setOpenMemberDialog(!openMemberDialog);
  };

  const handleTags = () => {
    handleClose();
    setOpenTagsDialog(!openTagsDialog);
  };

  const handlePreview = () => {
    setPreview(!preview);
  };

  const handleSave = async () => {
    loadingUpdate(true);
    await postSnapshots({ snapshots: JSON.stringify(snapshotDict) });
    getProjects();
    await getScopes(project.id);
    loadingUpdate(false);
    setPreview(!preview);
  };

  const handleDelete = async () => {
    handleClose();
    setOpenDeleteDialog(true);
  };

  const handleMove = async () => {
    setMove(!move);
  };

  // AI Suggest: Main handler for the AI button.
  // If suggestions exist, it saves them. Otherwise, it requests new suggestions.
  const handleMainAction = () => {
    if (hasSuggestions) {
      // If there are suggestions, button works as "Save"
      handleSaveAllSuggestions();
    } else {
      // Suggestion
      if (!project.pitch_link) {
        props.setSnack({
          open: true,
          severity: 'warning',
          message: 'This project doesn\'t have a pitch link. Please add a pitch link to use AI suggestions.',
        });
        return;
      }
      getScopesSuggestions(project.pitch_link, project.name, project.id);
    }
  };

  const handleDiscardSuggestion = (scopeId) => {
    setLocalScopes(prev => ({
      ...prev,
      scopes: prev.scopes.filter(s => s.id !== scopeId)
    }));
  };

  // Saves all current suggestions as real scopes in the backend
  const handleSaveAllSuggestions = async () => {
    setIsSavingSuggestions(true);
    loadingUpdate(true);

    try {
      const suggestions = localScopes.scopes.filter(s => s.is_suggestion);

      const allScopes = suggestions.map(suggestion => ({
        project_id: project.id,
        scope_name: suggestion.name,
        description: suggestion.description || "",
        created_by: userInfo.name,
        color: suggestion.color || "#FFFFFF",
        tasks: [
          ...(suggestion.tasks_ms || []).map(t => ({
            name: t.name,
            description: t.description || "",
            is_nice_to_have: false,
            supervisor: userInfo.id
          })),
          ...(suggestion.tasks_ns || []).map(t => ({
            name: t.name,
            description: t.description || "",
            is_nice_to_have: true,
            supervisor: userInfo.id
          }))
        ]
      }));

      const payload = {
        scopes: allScopes
      };

      await saveSuggestedScopes(payload);

      await getScopes(project.id);
      getProjects();
    } catch (error) {
      console.error('Error saving suggestions:', error);
    } finally {
      loadingUpdate(false);
      setIsSavingSuggestions(false);
    }
  };

  // Effect: Processes raw AI suggestions into local scope objects
  useEffect(() => {
    if (scopesSuggestions && scopesSuggestions.length > 0) {
      setLocalScopes(prev => {
        if (!prev || !prev.scopes) return prev;

        const suggestionsToProcess = scopesSuggestions.flat();
        const timestamp = Date.now();

        const newScopes = suggestionsToProcess.map((suggestion, index) => {
          // Generate a temporary negative ID for local handling
          const scopeId = -(timestamp + index);

          const processedTasksMs = [];
          const processedTasksNs = [];

          if (suggestion.tasks) {
            suggestion.tasks.forEach((task, tIndex) => {
              // Handle both string tasks and object tasks from the API
              let taskName = typeof task === 'string' ? task : (task.title || "");
              let taskDescription = typeof task === 'object' ? (task.description || "") : "";
              let isNiceToHave = false;

              if (taskName.trim().startsWith('~')) {
                isNiceToHave = true;
                taskName = taskName.replace('~', '').trim();
              }

              const taskObj = {
                id: -(timestamp + index * 1000 + tIndex + 50),
                name: taskName,
                description: taskDescription,
                is_completed: false,
                is_nice_to_have: isNiceToHave,
                order: tIndex + 1,
                scope_id: scopeId,
              };

              if (isNiceToHave) {
                processedTasksNs.push(taskObj);
              } else {
                processedTasksMs.push(taskObj);
              }
            });
          }

          return {
            id: scopeId,
            name: suggestion.name,
            description: suggestion.description,
            color: '#FFFFFF',
            order: prev.scopes.length + index + 1,
            completed_ms: 0,
            completed_ns: 0,
            completed_tasks: 0,
            total_tasks: processedTasksMs.length + processedTasksNs.length,
            tasks_ids: [],
            tasks_ns: processedTasksNs,
            tasks_ms: processedTasksMs,
            is_suggestion: true,
          };
        });

        return {
          ...prev,
          scopes: [...prev.scopes, ...newScopes],
        };
      });
    }
  }, [scopesSuggestions]);

  // Updates a task locally (in memory) for AI suggestions
  const handleLocalTaskUpdate = (scopeId, updatedTask) => {
    setLocalScopes(prev => {
      const newScopes = prev.scopes.map(s => {
        if (s.id !== scopeId) return s;

        const cleanMs = s.tasks_ms.filter(t => t.id !== updatedTask.id);
        const cleanNs = s.tasks_ns.filter(t => t.id !== updatedTask.id);

        if (updatedTask.is_nice_to_have) {
          cleanNs.push(updatedTask);
        } else {
          cleanMs.push(updatedTask);
        }

        return {
          ...s,
          tasks_ms: cleanMs,
          tasks_ns: cleanNs,
          total_tasks: cleanMs.length + cleanNs.length
        };
      });
      return { ...prev, scopes: newScopes };
    });
  };

  // Deletes a task locally (in memory) for AI suggestions
  const handleLocalTaskDelete = (scopeId, taskId) => {
    setLocalScopes(prev => {
      const newScopes = prev.scopes.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          tasks_ms: s.tasks_ms.filter(t => t.id !== taskId),
          tasks_ns: s.tasks_ns.filter(t => t.id !== taskId),
          total_tasks: s.total_tasks - 1
        };
      });
      return { ...prev, scopes: newScopes };
    });
  };

  // Updates scope details locally (in memory) for AI suggestions
  const handleLocalScopeUpdate = (scopeId, scopeData) => {
    setLocalScopes(prev => {
      const newScopes = prev.scopes.map(s => {
        if (s.id !== scopeId) return s;
        return { ...s, ...scopeData };
      });
      return { ...prev, scopes: newScopes };
    });
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination) return;

    // Helper to parse droppableId: "type_scopeId"
    const parseDroppableId = (droppableId) => {
      if (droppableId.startsWith('must_have_')) {
        return { type: 'must_have', scopeId: parseInt(droppableId.replace('must_have_', '')) };
      }
      if (droppableId.startsWith('nice_to_have_')) {
        return { type: 'nice_to_have', scopeId: parseInt(droppableId.replace('nice_to_have_', '')) };
      }
      return null;
    };

    const sourceData = parseDroppableId(source.droppableId);
    const destData = parseDroppableId(destination.droppableId);

    if (!sourceData || !destData) return;

    if (!localScopes) return;

    const newLocalScopes = {
      ...localScopes,
      scopes: localScopes.scopes.map(s => ({
        ...s,
        tasks_ms: [...s.tasks_ms],
        tasks_ns: [...s.tasks_ns]
      }))
    };

    const sourceScope = newLocalScopes.scopes.find(s => s.id === sourceData.scopeId);
    const destScope = newLocalScopes.scopes.find(s => s.id === destData.scopeId);

    if (!sourceScope || !destScope) return;

    const sourceListKey = sourceData.type === 'must_have' ? 'tasks_ms' : 'tasks_ns';
    const destListKey = destData.type === 'must_have' ? 'tasks_ms' : 'tasks_ns';

    // Get copies of the lists
    const sourceList = sortTasks(sourceScope[sourceListKey], sourceData.type);
    let destList = (sourceScope.id === destScope.id && sourceListKey === destListKey)
      ? sourceList
      : sortTasks(destScope[destListKey], destData.type);

    // Move the task
    const [movedTask] = sourceList.splice(source.index, 1);
    if (!movedTask) return;

    // Update task properties
    movedTask.is_nice_to_have = destData.type === 'nice_to_have';

    destList.splice(destination.index, 0, movedTask);

    // Update orders and counters
    sourceList.forEach((t, i) => t.order = i + 1);
    if (sourceList !== destList) {
      destList.forEach((t, i) => t.order = i + 1);
    }

    // Update counters if the task was completed
    if (movedTask.is_completed) {
      if (sourceData.type === 'must_have') {
        sourceScope.completed_ms = Math.max(0, sourceScope.completed_ms - 1);
      } else {
        sourceScope.completed_ns = Math.max(0, sourceScope.completed_ns - 1);
      }
      if (destData.type === 'must_have') {
        destScope.completed_ms = (destScope.completed_ms || 0) + 1;
      } else {
        destScope.completed_ns = (destScope.completed_ns || 0) + 1;
      }
    }

    sourceScope[sourceListKey] = sourceList;
    if (sourceScope.id !== destScope.id || sourceListKey !== destListKey) {
      destScope[destListKey] = destList;
    }

    // 2. Update local state immediately (Optimistic UI)
    setLocalScopes(newLocalScopes);

    // If it's a suggestion, stop here 
    if (sourceScope.is_suggestion || destScope.is_suggestion) {
      return;
    }

    // Prepare payload for updateTask API
    // API expects: [id, order, description, is_completed, is_nice_to_have, name, scope_id]
    const formatTasksForApi = (list, isNiceToHave, scopeId) => {
      return list.map((t, idx) => [
        t.id,
        idx + 1, // new order
        t.description,
        t.is_completed,
        isNiceToHave,
        t.name,
        scopeId
      ]);
    };

    // We need to update the source scope lists
    const sourcePayloadMs = formatTasksForApi(sortTasks(sourceScope.tasks_ms, 'must_have'), false, sourceScope.id);
    const sourcePayloadNs = formatTasksForApi(sortTasks(sourceScope.tasks_ns, 'nice_to_have'), true, sourceScope.id);

    let payload = [...sourcePayloadMs, ...sourcePayloadNs];

    // If scopes are different, we also need to update the destination scope lists
    if (sourceScope.id !== destScope.id) {
      const destPayloadMs = formatTasksForApi(sortTasks(destScope.tasks_ms, 'must_have'), false, destScope.id);
      const destPayloadNs = formatTasksForApi(sortTasks(destScope.tasks_ns, 'nice_to_have'), true, destScope.id);
      payload = [...payload, ...destPayloadMs, ...destPayloadNs];
    }

    loadingUpdate(true);
    await updateTask({ tasks: payload.flat() });
    await getScopes(project.id);
    loadingUpdate(false);
  };

  useEffect(() => {
    (async function () {
      const { loadingUpdate } = props;
      loadingUpdate(true);

      if (Object.keys(project).length === 0) {
        setIsLoadingProject(true);
        await getProjects();
      } else {
        setProjectExists(true);
      }
      setProjectsLoaded(true);
    })();
  }, []);

  // set project
  useEffect(() => {
    const initProject = async () => {
      if (
        Object.keys(project).length === 0 &&
        Object.keys(projects).length !== 0
      ) {
        let selectedProject = projects.filter(item => item.id == id);
        if (selectedProject.length === 0) {
          navigate('/projects');
        } else {
          await setProject(selectedProject['0']);
          setProjectExists(true);
        }
      }
    };
    if (projectsLoaded) {
      initProject();
    }
  }, [projectsLoaded]);

  // load component
  useEffect(() => {
    const loadComponent = () => {
      const { hasScopes, isLoadingScopes, getScopes, hasUsers, getUsers } =
        props;
      if (!hasScopes && !isLoadingScopes) {
        if (project.id) {
          getScopes(project.id);
        }
      }
      if (!hasUsers) {
        getUsers();
      }
      window.scrollTo(0, 0); // Scroll to top after a route transition
    };
    if (projectExists) {
      loadComponent();
    }
  }, [projectExists]);

  function resize() {
    if (
      Math.abs(window.innerWidth / 1.3 - width) > 200 ||
      Math.abs(window.innerHeight / 3.2 - height) > 10
    ) {
      setHeight(window.innerHeight / 3.2);
      setWidth(window.innerWidth / 1.3);
    }
  }

  if (hasScopes && hasUsers && localScopes && localScopes.scopes) {
    const dataPie = [];
    localScopes.scopes.forEach(element => {
      dataPie.push({
        name: element.name,
        value: element.total_tasks,
        fill: element.color,
      });
    });

    loadingUpdate(false);

    const history_length = localScopes.history_snapshots.length;
    let scopesList = localScopes.scopes;

    var completedScopes = [];
    var uncompletedScopes = [];

    scopesList.forEach(scope => {
      let scope_total_ms = 0;
      scope_total_ms = scope.tasks_ms.length;
      if (scope_total_ms !== scope.completed_ms || scope_total_ms === 0)
        uncompletedScopes.push(scope);
      else completedScopes.push(scope);
    });

    // now lets order the completed scopes
    uncompletedScopes.sort((a, b) => a.order - b.order);
    completedScopes.sort((a, b) => a.order - b.order);

    if (localScopes.history_snapshots[history_length + position]) {
      if (
        !runConfetti &&
        localScopes.history_snapshots[history_length + position].list.every(
          element => element.x === 100,
        ) &&
        scopesList.every(scope =>
          completedScopes
            .map(item => {
              return item.id;
            })
            .includes(scope.id),
        )
      ) {
        setRunConfetti(true);
      }
      if (
        runConfetti &&
        !localScopes.history_snapshots[history_length + position].list.every(
          element => element.x === 100,
        )
      ) {
        setRunConfetti(false);
      }
    }
    if (localScopes.history_snapshots[history_length + position]) {
      var snapshots = localScopes.history_snapshots[history_length + position].list;
    } else {
      snapshots = localScopes.history_snapshots;
    }
    var date = 'null';
    if (snapshots.length > 0) {
      snapshots.sort((b, a) => a.date - b.date);
      date = moment(snapshots[0].date * 1000).fromNow();
    }

    if (scopesList.length > 0) {
      scopesList.sort((b, a) => a.order - b.order);
    }

    //user role
    const role =
      project.members_roles[project.members_emails.indexOf(userInfo.email)];

    const clearSymbol = timeString => {
      var parts = timeString.split(':');
      var h = parts[0].includes('-') ? parts[0].slice(1) : parts[0];
      var m = parts[1].includes('-') ? parts[1].slice(1) : parts[1];
      var s = parts[2].includes('-') ? parts[2].slice(1) : parts[2];

      return [h, m, s].join(':');
    };

    const timestrToSec = timeString => {
      var parts = timeString.split(':');
      return parts[0] * 3600 + parts[1] * 60 + +parts[2];
    };

    const data = localScopes.time_bar_data.map(item => ({ ...item }));

    data.forEach((item, idx, array) => {
      let start = clearSymbol(item.start);
      let end = clearSymbol(item.end);
      let newStart = start;
      let newEnd = end;

      if (0 < idx && idx < data.length - 1) {
        let startSec = timestrToSec(start);
        let endSec = timestrToSec(end);

        //if time not sorted then sort
        if (startSec > endSec) {
          // change actual
          newStart = end;
          newEnd = start;
          // change next
          array[idx + 1].start = newEnd;
          // change prev
          array[idx - 1].end = newStart;
        }
      }
      array[idx].start = newStart;
      array[idx].end = newEnd;
    });

    for (let i = 0; i < data.length; i++) {
      if (i - history_length === position) {
        data[i].color = 'lightgreen';
      } else {
        data[i].color = data[i].color_fixed;
      }
      data[i].children = (
        <Tooltip
          key={position}
          title={'Update ' + (i + 1)}
          arrow
          placement={i - history_length === position ? 'bottom' : 'top'}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: data[i].color,
                '& .MuiTooltip-arrow': {
                  color: data[i].color,
                },
              },
            },
          }}>
          <div>
            <Tooltip
              open={i - history_length === position}
              key={position}
              title={'Update ' + (i + 1)}
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: data[i].color,
                    '& .MuiTooltip-arrow': {
                      color: data[i].color,
                    },
                  },
                },
              }}>
              <div>
                <div
                  className="timebar-item"
                  onClick={() => setPosition(i - history_length)}
                  style={{ cursor: 'pointer' }}>
                  &zwnj;
                </div>
              </div>
            </Tooltip>
          </div>
        </Tooltip>
      );
    }

    return (
      <React.Fragment>
        <Box sx={{ display: 'flex' }}>
          <Toolbar id={'back-to-top-anchor'} sx={{ position: 'absolute' }} />
          <Paper
            elevation={3}
            sx={{ marginTop: '1em' }}
            style={{
              width: '100%',
              height: '100%',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              userSelect: 'none',
            }}>
            <Grid container>
              <Grid item xs={3}>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  marginLeft={'1em'}
                  marginTop={'1em'}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/projects')}
                    startIcon={<ArrowBackIcon />}
                    size="small">
                    Go back
                  </Button>
                </Box>
              </Grid>
              <Grid container item xs={6}>
                <Grid item xs={12}>
                  <Typography variant="h5" align="center" marginTop={2}>
                    {project.name.charAt(0).toUpperCase() +
                      project.name.slice(1)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    component="div"
                    align="center"
                    marginBottom={'1'}>
                    <b>Start date:</b>{' '}
                    {moment(project.p_init).local().calendar()} (
                    {moment(project.p_init).fromNow()})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    component="div"
                    align="center"
                    marginBottom={'1'}>
                    <b>Finish date:</b>{' '}
                    {moment(project.p_due).local().calendar()} (
                    {moment(project.p_due).fromNow()})
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={2}>
                <Box justifyContent="flex-start" display="flex" marginTop={2}>
                  <PieChart
                    width={60}
                    height={60}
                    style={{ position: 'relative' }}>
                    <Pie
                      data={dataPie.sort((a, b) => {
                        return b.value - a.value;
                      })}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={'120%'}
                    />
                    <Tol position={{ x: -80, y: 70 }} />
                  </PieChart>
                </Box>
              </Grid>
              <Grid item xs={1}>
                {role === 'Project Manager' || admin ? (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    marginRight={'1em'}
                    marginTop={'1em'}>
                    <Button
                      variant="outlined"
                      onClick={handleClick}
                      endIcon={<MenuIcon />}
                      aria-controls={openMenu ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                      size="small">
                      Menu
                    </Button>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleClose}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button',
                      }}>
                      <MenuItem
                        disabled={!project.pitch_link}
                        onClick={() =>
                          window.open(project.pitch_link, '_blank')
                        }>
                        Project pitch
                      </MenuItem>
                      <MenuItem onClick={handleMembers}>Edit members</MenuItem>
                      <MenuItem onClick={handleTags}>Edit tags</MenuItem>
                      <MenuItem onClick={handleEdit}>Edit project</MenuItem>
                      <MenuItem onClick={handleDelete}>Delete project</MenuItem>
                    </Menu>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    marginRight={'1em'}
                    marginTop={'1em'}>
                    <Button
                      variant="outlined"
                      onClick={handleClick}
                      endIcon={<MenuIcon />}
                      aria-controls={openMenu ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                      size="small">
                      Menu
                    </Button>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleClose}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button',
                      }}>
                      <MenuItem
                        disabled={!project.pitch_link}
                        onClick={() =>
                          window.open(project.pitch_link, '_blank')
                        }>
                        Project pitch
                      </MenuItem>
                    </Menu>
                  </Box>
                )}
              </Grid>
            </Grid>
            <Box margin={'1em'}>
              <Divider />
            </Box>
            <Grid container marginTop={'1em'}>
              <Grid item xs={4}>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  marginLeft={'1em'}>
                  {showHistory ? (
                    <Button
                      variant="outlined"
                      onClick={handleHistory}
                      startIcon={<CloseIcon />}
                      size="small">
                      Close
                    </Button>
                  ) : (
                    <></>
                  )}
                  {(role === 'Member' || admin) && !showHistory ? (
                    preview ? (
                      <Button
                        variant="outlined"
                        onClick={handlePreview}
                        startIcon={<EditIcon />}
                        size="small">
                        Update
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={handlePreview}
                        startIcon={<EditOffIcon />}
                        size="small">
                        Cancel
                      </Button>
                    )
                  ) : (
                    <></>
                  )}
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" component="div" align="center">
                  {position === -1 ? <b>Last updated: </b> : <b>Updated: </b>}
                  {snapshots[0]
                    ? date.charAt(0).toUpperCase() + date.slice(1)
                    : 'null'}{' '}
                  by {snapshots[0] ? snapshots[0].created_by : 'null'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  marginRight={'1em'}>
                  {!preview ? (
                    <Button
                      variant="outlined"
                      onClick={handleSave}
                      endIcon={<SaveIcon />}
                      size="small">
                      Save
                    </Button>
                  ) : showHistory ? (
                    <>
                      <Button
                        variant="outlined"
                        disabled={position === -history_length ? true : false}
                        onClick={() => handleForwardLeft(history_length)}
                        startIcon={<KeyboardDoubleArrowLeftIcon />}
                        size="small"></Button>
                      <Button
                        variant="outlined"
                        disabled={
                          position - 1 >= -history_length ? false : true
                        }
                        onClick={() => handleLeft(history_length)}
                        startIcon={<NavigateBeforeIcon />}
                        size="small"></Button>
                      <Button
                        variant="outlined"
                        disabled={position + 1 < 0 ? false : true}
                        onClick={handleRight}
                        endIcon={<NavigateNextIcon />}
                        size="small"></Button>
                      <Button
                        variant="outlined"
                        disabled={position === -1 ? true : false}
                        onClick={() => handleForwardRight()}
                        startIcon={<KeyboardDoubleArrowRightIcon />}
                        size="small"></Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleHistory}
                      endIcon={<TimelineIcon />}
                      size="small">
                      History
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  marginRight={'1em'}>
                  <Chart
                    snapshots={snapshots}
                    width={width}
                    height={height}
                    preview={preview}
                    created_by={username}
                    project_id={project.id}
                    setSnapshotDict={setSnapshotDict}
                    isChangable={true}
                    completedScopes={completedScopes.map(item => {
                      return item.id ? item.id : -1;
                    })}
                    key={width + height + preview + snapshots + position}
                  />
                </Box>
              </Grid>
              {showHistory ? (
                <Grid item xs={12}>
                  <Box
                    margin={2}
                    marginLeft={'5em'}
                    marginRight={'5em'}
                    marginBottom={'2em'}>
                    <TimeProgressBar
                      hourBar={<></>}
                      props={{
                        style: { height: 10 },
                      }}
                      data={data}
                    />
                  </Box>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
            <Box margin={'1em'}>
              <Divider>TO-DO</Divider>
            </Box>
            <Grid container marginTop={'1em'}>
              <Grid item xs={6}>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  marginLeft={'1em'}
                  marginBottom={'1em'}>
                  {role === 'Member' || admin ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={openScope}
                        startIcon={<PlaylistAddIcon />}
                        size="small"
                      >
                        Add scope
                      </Button>
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  marginRight={'1em'}
                  marginBottom={'1em'}>
                  {role === 'Member' || admin ? (
                    <>
                      <IconGradientDefinition />
                      <Tooltip title="Requires pitch link to the specific slide & project name must match PPT">
                        <span>
                          <Button
                            onClick={handleMainAction}
                            size="small"
                            disabled={isScopesSuggestionsLoading || isLoadingTaskSuggestions || isSavingSuggestions}
                            startIcon={
                              isSavingSuggestions ? (
                                <AutoAwesomeIcon />
                              ) : isScopesSuggestionsLoading ? (
                                <AutoAwesomeIcon />
                              ) : hasSuggestions ? (
                                <DoneAllIcon />
                              ) : (
                                <AutoAwesomeIcon />
                              )
                            }
                            sx={{
                              mr: 1,
                              minWidth: '160px',
                              ...getIAStyles('button', isScopesSuggestionsLoading || isSavingSuggestions)
                            }}
                          >
                            {isSavingSuggestions ? (
                              'SAVING...'
                            ) : isScopesSuggestionsLoading ? (
                              'GENERATING...'
                            ) : hasSuggestions ? (
                              'SAVE CHANGES'
                            ) : (
                              'AI SUGGEST'
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        onClick={handleMove}
                        endIcon={move ? <CloseIcon /> : <ReorderIcon />}
                        size="small">
                        {move ? 'Stop moving' : 'Move scopes'}
                      </Button>
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </Grid>
            </Grid>
            {move ? (
              <>
                <Box marginLeft={'1em'} marginRight={'1em'} marginTop={'1em'}>
                  <DragabbleCard
                    project={project}
                    created_by={username}
                    users={users}
                    role={role}
                    scopes={uncompletedScopes}
                    loadingUpdate={loadingUpdate}
                    getScopes={getScopes}
                    updateScopes={updateScopes}
                  />
                </Box>
                <Box margin={'1em'} paddingBottom={'1em'}>
                  <Divider>DONE</Divider>
                </Box>
                <Box marginLeft={'1em'} marginRight={'1em'} marginTop={'1em'}>
                  <DragabbleCard
                    project={project}
                    created_by={username}
                    users={users}
                    role={role}
                    scopes={completedScopes}
                    loadingUpdate={loadingUpdate}
                    getScopes={getScopes}
                    updateScopes={updateScopes}
                  />
                </Box>
              </>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Box marginLeft={'1em'} marginRight={'1em'} marginTop={'1em'}>
                  <Grid
                    container
                    justifyContent="left"
                    alignItems="left"
                    columns={{ xs: 4, sm: 8, md: 12 }}>
                    {uncompletedScopes.map(scope => (
                      <Grid item xs={4} key={scope.id}>
                        <Scope
                          scope={scope}
                          created_by={username}
                          users={users}
                          role={role}
                          project={project}
                          onDiscard={handleDiscardSuggestion}
                          onLocalUpdate={handleLocalTaskUpdate}
                          onLocalDelete={handleLocalTaskDelete}
                          onLocalScopeUpdate={handleLocalScopeUpdate}
                          isSuggesting={suggestingScopeId === scope.id}
                          onSuggest={() => setSuggestingScopeId(scope.id)}
                          onSuggestComplete={() => setSuggestingScopeId(null)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Box margin={'1em'} paddingBottom={'1em'}>
                  <Divider>DONE</Divider>
                </Box>
                <Box marginLeft={'1em'} marginRight={'1em'} marginTop={'1em'}>
                  <Grid
                    container
                    justifyContent="left"
                    alignItems="left"
                    columns={{ xs: 4, sm: 8, md: 12 }}>
                    {completedScopes.map(scope => (
                      <Grid item xs={4} key={scope.id}>
                        <Scope
                          scope={scope}
                          project={project}
                          created_by={username}
                          users={users}
                          role={role}
                          isSuggesting={suggestingScopeId === scope.id}
                          onSuggest={() => setSuggestingScopeId(scope.id)}
                          onSuggestComplete={() => setSuggestingScopeId(null)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </DragDropContext>
            )}
          </Paper>
          <AddScopeDialog
            open={openScopeDialog}
            project_id={project.id}
            created_by={username}
            scope_len={localScopes.scopes.length}
            closeScope={() => closeScope()}
          />
          <DeleteDialog
            open={openDeleteDialog}
            toDelete={project.id}
            toDeleteName={'project'}
            deleteFunction={deleteProject}
            getFunction={getProjects}
            loadingUpdate={loadingUpdate}
            closeDialog={setOpenDeleteDialog}
            goHome={true}
          />
          <EditProjectDialog
            open={openEditDialog}
            closeDialog={setOpenEditDialog}
            project={project}
          />
          <EditMembersDialog
            open={openMemberDialog}
            closeDialog={setOpenMemberDialog}
            project={project}
          />
          <EditTagsDialog
            open={openTagsDialog}
            closeDialog={setOpenTagsDialog}
            project={project}
          />
          <Confetti
            width={confettiWidth}
            height={confettiHeight}
            run={runConfetti}
            numberOfPieces={200}
            recycle={false}
            gravity={0.4}
            key={runConfetti}
          />
          <ScrollToTop {...props} />
        </Box>
      </React.Fragment>
    );
  } else {
    if (isLoadingProject || isLoadingScopes) {
      return <></>;
    } else {
      return <></>;
    }
  }
}
